// Registers the service worker (needed for install + push). Safe no-op if unsupported.
import { registerSW } from "virtual:pwa-register";
import { isProductionHost } from "../shared/origin";

export function registerServiceWorker(): void {
  // NON-production origins (PR-preview hosts, localhost) must never run a service worker: a
  // precached app shell from an earlier build gets served long after the code changed (the app
  // this was lifted from showed reviewers a screen that had been deleted weeks before).
  // On these origins we refuse to register AND actively rip out any SW + caches a prior preview left
  // in this browser, so previews always load fresh from the network. (The old SW on an
  // already-affected browser tears itself down from inside sw.ts on its next update check; this is
  // the belt-and-braces client path for once the fresh bundle is running.)
  if (!isProductionHost(window.location.hostname)) {
    void teardownServiceWorker();
    return;
  }

  if (!("serviceWorker" in navigator)) return;

  // When a new deploy's service worker takes control, reload once so the page runs the fresh
  // code instead of the old in-memory bundle. Without this, `skipWaiting()`+`clients.claim()`
  // hand control to the new SW but the already-loaded (stale) page keeps executing — the
  // classic "deployed but users still see the old app" problem, especially on installed iOS
  // PWAs. We only reload when a controller already existed (i.e. a genuine update, not the
  // first-ever install), and guard against reload loops. The reload waits while someone is
  // typing or holding a button, so an update never eats a half-typed email or a minus tap.
  let reloading = false;
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading || !hadController) return;
    reloading = true;
    reloadWhenIdle();
  });

  registerSW({
    // Registered after the load event, so the first visit's precache download does not
    // compete with sign-in on a slow connection.
    immediate: false,
    // An installed app can stay open for days; look for a new deploy each time it comes back.
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void registration.update().catch(() => undefined);
      });
    },
  });
}

/** True while a text field holds something or a hold-to-confirm button is being held. */
function isBusy(): boolean {
  if (document.querySelector(".hold[data-holding]")) return true;
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "input:not([type=radio]):not([type=checkbox]), textarea",
  );
  return Array.from(fields).some((f) => f.value !== "");
}

/** Reloads now if nobody is mid-input, else as soon as they are done or the app is hidden. */
function reloadWhenIdle(): void {
  if (!isBusy()) {
    window.location.reload();
    return;
  }
  const timer = window.setInterval(() => {
    if (isBusy() && document.visibilityState === "visible") return;
    window.clearInterval(timer);
    window.location.reload();
  }, 1000);
}

// Remove any service worker + caches left by a previous build on a non-production origin, then
// reload once if a SW was actively controlling this page (so we drop the stale precached shell and
// re-fetch from the network). Feature-guarded, so a safe no-op in unsupported browsers. No reload
// loop: after unregister the reloaded page has no controller, so `hadController` is false next run.
async function teardownServiceWorker(): Promise<void> {
  const hadController = "serviceWorker" in navigator && !!navigator.serviceWorker.controller;

  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  }

  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }

  if (hadController) window.location.reload();
}
